"use client";

import { useEffect, useState } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import { useDispatch, useSelector } from "react-redux";
import CustomDataTable from "../utils/data-tables/CustomDataTable";
import {
  getExpertQueriesAction,
  updateQueryStatusAction,
} from "../../services/action/common";
import { ColumnQueries } from "./ColumnQueries";
import CustomModal from "../utils/modals/CustomModal";
import ShowMessage from "./ShowMessage";
import { handlePopupModalAction } from "../../services/action/event";
import { Menu } from "lucide-react";

const Queries = () => {
  const { user } = useSelector((state) => state.authenticationReducer);
  const { queriesList } = useSelector((state) => state.commonReducer);
  const [recentResolved, setRecentResolved] = useState([]);
  const [search, setSearch] = useState("");
  const [pageData, setPageData] = useState({
    page: 1,
    limit: 15,
  });
  const dispatch = useDispatch();
  const [showMessage, setShowMessage] = useState("");

  const getList = ({ page = 1, firstTime = false, tempSearch = search }) => {
    dispatch(
      getExpertQueriesAction({
        userType: user?.userType?.toLowerCase(),
        page,
        data: { searchQuery: tempSearch },
        firstTime,
      })
    );
  };

  useEffect(() => {
    if (user) {
      getList({ page: 1, firstTime: true });
    }
  }, [dispatch, user]);

  const fetchMoreData = () => {
    setPageData((prev) => ({ page: prev.page + 1 }));
    getList({ page: pageData?.page + 1 });
  };

  const updateQueryStatus = ({ leadId, queryId }) => {
    const tempObj = {
      userType: user?.userType?.toLowerCase(),
      data: {
        leadId,
        queryId,
      },
      cb: () => setRecentResolved((prev) => [...prev, queryId]),
    };
    dispatch(updateQueryStatusAction(tempObj));
  };

  return (
    <DashboardLayout
      onSearch={(value) => {
        setSearch(value);
        setPageData({ page: 1 });
        getList({ page: 1, firstTime: true, tempSearch: value });
      }}
    >
      <div className="w-full flex flex-col space-y-4">
        {/* Header Section */}

        {/* Data Table Section */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-wrap items-center justify-between">
              <button
                onClick={() =>
                  dispatch(handlePopupModalAction({ isOpenSidebar: true }))
                }
                className="p-2 hover:text-white rounded-md hover:bg-[#0A2547] transition-colors lg:hidden"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-lg font-semibold text-gray-800">
                Query List
              </h2>

              <div className="flex items-center space-x-4 mt-2 sm:mt-0">
                <div className="flex items-center space-x-2">
                  <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-xs text-gray-600">Resolved</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-block w-3 h-3 rounded-full bg-orange-500"></span>
                  <span className="text-xs text-gray-600">Pending</span>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <CustomModal
              open={showMessage}
              onClose={() => setShowMessage("")}
              content={() => <ShowMessage showMessage={showMessage} />}
            />

            <div className="min-h-[400px]">
              <CustomDataTable
                fetchMoreData={fetchMoreData}
                isInfinitScroll={true}
                columns={ColumnQueries(
                  setShowMessage,
                  updateQueryStatus,
                  recentResolved
                )}
                data={queriesList?.data}
              />
            </div>

            {(!queriesList?.data || queriesList?.data.length === 0) && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80">
                <div className="text-center p-6">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 text-gray-400 mx-auto mb-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900">
                    No queries found
                  </h3>
                  <p className="text-gray-500 mt-2">
                    When experts submit queries, they will appear here.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Queries;
